import { useRef, useState } from "react";
import { ImagePlus, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { BTN, DIALOG } from "@/components/account/ConfirmIdentity";

const COPY = {
  support: { title: "Contact support", text: "Ask a question about your account, plan or how something works.", subject: "What do you need help with?" },
  problem: { title: "Report a problem", text: "Tell us what went wrong. Screenshots help us fix it faster.", subject: "What went wrong, in a few words?" },
};

function Shots({ files, setFiles }) {
  const ref = useRef(null);
  const add = (e) => {
    const picked = [...(e.target.files || [])].filter((f) => /^image\/(png|jpeg|webp)$/.test(f.type));
    e.target.value = "";
    if (picked.some((f) => f.size > 5 * 1024 * 1024)) toast.error("Each screenshot must be under 5 MB.");
    setFiles((x) => [...x, ...picked.filter((f) => f.size <= 5 * 1024 * 1024)].slice(0, 3));
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {files.map((f, i) => (
          <span key={`${f.name}-${f.lastModified}`} className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-white/[0.06] text-neutral-300" data-testid={`support-shot-${i}`}>
            <span className="max-w-[140px] truncate">{f.name}</span>
            <button type="button" onClick={() => setFiles((x) => x.filter((_, j) => j !== i))} aria-label={`Remove ${f.name}`} data-testid={`support-shot-remove-${i}`}><X className="h-3 w-3" /></button>
          </span>
        ))}
      </div>
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" multiple hidden onChange={add} data-testid="support-shot-input" />
      {files.length < 3 && <button type="button" onClick={() => ref.current?.click()} className="text-sm text-neutral-400 hover:text-white flex items-center gap-1.5" data-testid="support-shot-add-btn"><ImagePlus className="h-4 w-4" />Add screenshots (up to 3)</button>}
    </div>
  );
}

export function SupportDialog({ kind, onClose }) {
  const { user } = useAuth();
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [page, setPage] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const c = COPY[kind];
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    const fd = new FormData();
    fd.append("kind", kind);
    fd.append("subject", subject);
    fd.append("message", message);
    fd.append("page", page);
    files.forEach((f) => fd.append("files", f));
    try {
      const { data } = await http.post("/support", fd);
      toast.success(data.emailed ? `Sent. We'll reply to ${user.email}.` : "Saved. Our email is busy right now, but we have your message.");
      onClose();
    } catch (err) { toast.error(formatError(err)); setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={`${DIALOG} max-h-[90dvh] overflow-y-auto`} data-testid={`support-dialog-${kind}`}>
        <DialogHeader>
          <DialogTitle className="font-display">{c.title}</DialogTitle>
          <DialogDescription className="text-neutral-400">{c.text} We reply by email to {user?.email}.</DialogDescription>
        </DialogHeader>
        <form onSubmit={send} className="space-y-3">
          <Input value={subject} maxLength={120} onChange={(e) => setSubject(e.target.value)} placeholder={c.subject} aria-label="Subject" className="bg-white/5 border-white/10" data-testid="support-subject-input" />
          <Textarea value={message} maxLength={5000} rows={5} onChange={(e) => setMessage(e.target.value)} placeholder="Tell us more" aria-label="Message" className="bg-white/5 border-white/10" data-testid="support-message-input" />
          {kind === "problem" && (
            <>
              <Input value={page} maxLength={300} onChange={(e) => setPage(e.target.value)} placeholder="Where did it happen? (optional, e.g. a chat or Practice)" aria-label="Where it happened" className="bg-white/5 border-white/10" data-testid="support-page-input" />
              <Shots files={files} setFiles={setFiles} />
            </>
          )}
          <button type="submit" disabled={busy || subject.trim().length < 3 || message.trim().length < 10} className={BTN} data-testid="support-send-btn">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}Send
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
